import { combine, createEvent, createStore } from 'effector';
import type { SightId } from '../types';
import type { TourItem } from '../shared/assistantMessage';
import { $sceneId } from './appState';

export type TourSightEntry = { sightId: SightId; visited: boolean };

type SceneTourCacheEntry = {
  sights: TourSightEntry[];
  /**
   * `scene_id` из tour-item `{ kind: 'scene' }` — id следующей сцены, в
   * которую бек предлагает перейти после прохождения всех POI текущей
   * сцены. `null`, если бек не прислал такой item. Хранится как есть
   * (бек присылает backend-id / URL-id / inside-id) — нормализация в
   * URL-id для navigation выполняется на стороне UI через
   * `resolveNextSceneTarget`.
   */
  nextSceneId: string | null;
};

type SceneTourCache = Map<string, SceneTourCacheEntry>;

/**
 * Where a tour update came from:
 *   - `'navigation'` (default when omitted) — user-driven scene_open /
 *     message reply. Analytics milestone trackers (progressTracker) react.
 *   - `'prefetch'` — headless menu-progress prefetch (`sceneProgress.ts`).
 *     Pure data: updates `$tourByScene` for the menu counters but MUST stay
 *     invisible to analytics, so progressTracker skips it.
 */
export type TourOrigin = 'navigation' | 'prefetch';

export type TourReceivedPayload = {
  sceneId: string;
  items: TourItem[];
  /** Defaults to `'navigation'` when omitted. */
  origin?: TourOrigin;
};

/**
 * Dispatched whenever an `assistant_message` arrives with a `tour` array. The
 * payload carries the `sceneId` in effect at receipt time so the tour is cached
 * per-scene — re-entering the same scene later in the session restores the
 * last-known visited state without waiting for a fresh backend message.
 *
 * The cache lives for the tab session: it is intentionally NOT wired to
 * `assistantChatReset` because most "between main scenes" transitions go
 * through `resetNavigation()` (= chat reset), which would otherwise wipe the
 * visited cache exactly when the user expects it to persist.
 */
export const tourReceived = createEvent<TourReceivedPayload>();

const EMPTY_ENTRY: SceneTourCacheEntry = { sights: [], nextSceneId: null };

export const normalizeTour = (items: TourItem[]): SceneTourCacheEntry => {
  const sights: TourSightEntry[] = [];
  let nextSceneId: string | null = null;
  for (const it of items) {
    if (it.kind === 'sight') {
      sights.push({ sightId: it.sight_id, visited: it.visited });
    } else if (it.kind === 'scene' && nextSceneId === null) {
      // Берём первый scene-item на случай, если бек по ошибке вернёт несколько.
      nextSceneId = it.scene_id;
    }
  }
  return { sights, nextSceneId };
};

const $tourByScene = createStore<SceneTourCache>(new Map()).on(
  tourReceived,
  (map, { sceneId, items }) => {
    const next = new Map(map);
    next.set(sceneId, normalizeTour(items));
    return next;
  },
);

/**
 * Full ordered tour entries for the active scene, sourced from the
 * cached backend tour. Order follows the tour array as-is (both visited
 * and unvisited items, in their original positions). This is the
 * authoritative ordering for `PoiChips` — `AssistantHud.poiLocations`
 * derives `unvisited` and `visited` slices from it. `orderedSightIds`
 * from `/api/config.pois` is used only as fallback for sights that
 * are present in `sceneSights` but missing from the tour entirely
 * (shouldn't happen if backend stays consistent — defence in depth).
 */
export const $tourEntriesForCurrentScene = combine(
  $tourByScene,
  $sceneId,
  (map, sceneId): TourSightEntry[] => {
    if (sceneId === null) return [];
    return (map.get(String(sceneId)) ?? EMPTY_ENTRY).sights;
  },
);

/**
 * Ordered list of visited sight IDs for the active scene. Tour-ordered.
 * Kept for backward compatibility — internally derived from
 * `$tourEntriesForCurrentScene`.
 */
export const $visitedSightIdsForCurrentScene = $tourEntriesForCurrentScene.map(
  (entries) => entries.filter((e) => e.visited).map((e) => e.sightId),
);

export const $visitedSightIdsSet = $visitedSightIdsForCurrentScene.map(
  (ids) => new Set<SightId>(ids),
);

/**
 * Backend-supplied next-scene id for the active scene (from the
 * `tour: [..., { kind: 'scene', scene_id }]` item).
 *
 * Drives the golden "Next scene – {name}" chip in `PoiChips`. The id
 * is returned as-is (backend-id / URL-id / inside-id all possible).
 * UI normalization for navigation + label lookup lives in
 * `resolveNextSceneTarget`.
 *
 * Self-guard: returns `null` if the backend echoes the current scene
 * (avoids a degenerate "go to where you already are" loop).
 */
export const $nextSceneIdForCurrentScene = combine(
  $tourByScene,
  $sceneId,
  (map, sceneId): string | null => {
    if (sceneId === null) return null;
    const entry = map.get(String(sceneId));
    if (!entry || entry.nextSceneId === null) return null;
    if (entry.nextSceneId === String(sceneId)) return null;
    return entry.nextSceneId;
  },
);

export type SceneProgress = {
  visited: number;
  total: number;
  /** `true` once a backend tour has been received for this scene. */
  hasData: boolean;
};

/**
 * Per-scene visited/total POI counts derived from the cached backend tour,
 * keyed by poiFilterId (the SAME key `$tourByScene` uses — `main`,
 * `arafat-scene`, `kaaba-inside`, …). Scenes without a cached tour are
 * simply absent from the record; callers treat a missing entry as
 * "no data yet" (and may trigger a prefetch — see `sceneProgress.ts`).
 *
 * Drives the menu's "{visited} of {total} spots" counters + the
 * inside-scene tree badges + the overall journey progress aggregate.
 */
export const $sceneProgressByPoiFilterId = $tourByScene.map((map) => {
  const result: Record<string, SceneProgress> = {};
  for (const [sceneId, entry] of map) {
    const total = entry.sights.length;
    const visited = entry.sights.reduce((n, s) => (s.visited ? n + 1 : n), 0);
    result[sceneId] = { visited, total, hasData: true };
  }
  return result;
});
